import React from 'react';
import withRouter from 'with-router';
import { connect } from 'react-redux';
import withStyles from '../../../withStylesAdapter';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Link } from 'react-router-dom';
import { withTranslation, Trans } from 'react-i18next';
import { BulkTranslationSettings, BULK_SUB_FIELD_LABELS } from '../../../components/BulkTranslations.js';
import RechargePlans from '../../../components/RechargePlans.js';
import { formatLargeNumber, formatDate } from '../../../helper.js';
import { getBulkSettings, resolveBulkTranslationType } from '../../../modules/config.js';
import API from '../../../api/api.js';
import SolutionsArticleLink from '../../../components/SolutionsArticleLink';
import { solutionsArticleUrl } from '../../../config/solutionsArticles';

const REVIEW_POINTS = ['reviewGlossary', 'reviewPages', 'reviewLegal'];
const REVIEW_COMPONENTS = {
  glossary: <Link to="/machinetranslation/glossary" />,
};

class MachineTranslations extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showRechargePlans: false,
      config: null,
      wordsLog: [],
      wordsLogLoading: false,
      wordsLogHasMore: false,
    };
    this.handleConfigChange = this.handleConfigChange.bind(this);
  }

  componentDidMount() {
    this.props.getBulkSettings();
    this.setState({ wordsLogLoading: true });
    this.loadWordsLog();
  }

  // The balance is refreshed when a bulk run starts and when it finishes; the log below it only
  // ever came from the mount, so the card showed a new balance over a list missing its entry.
  // An AI run costs nothing, so the balance alone misses it: the finish sync flipping back is
  // the second trigger.
  componentDidUpdate(prevProps) {
    const before = prevProps.shop?.shop?.auto_translations?.available_words;
    const now = this.props.shop?.shop?.auto_translations?.available_words;
    const syncEnded = prevProps.bulkTranslation?.is_synchronizing && !this.props.bulkTranslation?.is_synchronizing;

    if(before !== now || syncEnded) {
      this.loadWordsLog();
    }
  }

  // No pending flag on purpose: a refresh leaves the list standing instead of swapping it for
  // the spinner.
  loadWordsLog() {
    API.getAvailableWordsLog(1, 5, (res) => {
      this.setState({
        wordsLog: (res && res.log) || [],
        wordsLogLoading: false,
        wordsLogHasMore: !!(res && res.totalPages > 1),
      });
    });
  }

  handleConfigChange({ languages, fields, outdated }) {
    const { languages: languagesData } = this.props;
    const resolvedLanguages = languages.map(code => {
      const lang = languagesData?.languages?.find(l => l.code?.toLowerCase() === code.toLowerCase());
      return lang ? { code, name: lang.name } : { code, name: code };
    });
    // the fields arrive in tick order; the products sub-fields sit right behind "products",
    // as in the words log
    const subFields = fields.filter(f => f.startsWith('products.'));
    if(subFields.length > 0) {
      const rest = fields.filter(f => !f.startsWith('products.'));
      const at = rest.indexOf('products');
      fields = at === -1 ? [...rest, ...subFields] : [...rest.slice(0, at + 1), ...subFields, ...rest.slice(at + 1)];
    }
    const hasContent = languages.length > 0 || fields.length > 0;
    this.setState({ config: hasContent ? { resolvedLanguages, fields, outdated } : null });
  }

  render() {
    const { t, classes, shop, themes, bulkTranslation, bulkSettingsData, theme } = this.props;
    const { showRechargePlans, config, wordsLog, wordsLogLoading, wordsLogHasMore } = this.state;

    // Effective bulk translation engine (falls back to NMT/default without the AI beta
    // feature or below the Growth plan).
    const bulkType = resolveBulkTranslationType(bulkSettingsData, shop?.shop?.charge?.name, shop?.shop?.langify_features);
    const isAiBulk = bulkType === 'ai';

    // Word balance
    const autoTranslations = shop?.shop?.auto_translations || {};
    const availableWords = Math.max(autoTranslations.available_words || 0, 0);
    const totalUsedWords = autoTranslations.total_used_words || 0;

    const bulkTotalWords = bulkTranslation?.total_words || 0;
    const countDone = !bulkTranslation?.count_words_pending && bulkTranslation?.count_progress === 100 && bulkTotalWords > 0;
    const missingWords = countDone && bulkTotalWords > availableWords ? bulkTotalWords - availableWords : 0;

    /**
     * Eskalationsfarben des Wortstands. Die Schwellen bleiben unverändert, nur
     * die Farben laufen jetzt über die Palette — die alten Werte trugen in
     * jeweils einem Modus nicht: '#9e9e00' und '#ff9800' lagen im LIGHT-Modus
     * bei 2.86:1 bzw. 2.16:1 auf weisser Karte, 'green' im Dark Mode bei
     * 3.25:1. Über Tokens liegen alle vier Stufen in beiden Modi über der
     * 3:1-Schwelle, die für diese h5-Zahl (24px) gilt.
     */
    let wordsColor = theme.palette.success.main;
    if (availableWords < 250) wordsColor = theme.palette.warning.main;
    if (availableWords < 150) wordsColor = theme.palette.warning.dark;
    if (availableWords < 50) wordsColor = theme.palette.error.main;

    const onBuyWords = (plan) => {
      API.buyWords(plan, () => {});
    };

    // a default outlined chip is dark on the purple page heading; the coloured AI chip stays
    const onBannerChip = { color: 'var(--ly-banner-text)', borderColor: 'var(--ly-banner-text)' };

    return (
      <React.Fragment>
        {/* page-heading: the banner colour rule in index.css reaches the h2 inside the flex box */}
        <div className="page-heading" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Typography
            variant="h4"
            gutterBottom
            component="h2"
            style={{ marginBottom: 0 }}
          >
            {t('navigation.primary.bulkSettings')}
          </Typography>
          {isAiBulk ? (
            <div className={classes.chipRow} style={{ marginTop: 0 }}>
              <Chip
                icon={<AutoAwesomeOutlinedIcon />}
                color="success"
                variant="outlined"
                label={t('bulkTranslationFeature.settingsPage.typeBadgeAi', 'AI translation · free')}
                // the banner is purple in both modes; the light palette's green is too dark on it,
                // so the chip borrows the dark palette's lighter tone there
                sx={(theme) => ({
                  fontWeight: 500,
                  ...(theme.palette.mode === 'light'
                    ? { color: '#a5d6a7', borderColor: '#a5d6a7', '& .MuiChip-icon': { color: '#a5d6a7' } }
                    : {}),
                })}
              />
              <Chip
                variant="outlined"
                label={t('system.beta', 'Beta')}
                sx={{ fontWeight: 500, ...onBannerChip }}
              />
            </div>
          ) : (
            <Chip
              variant="outlined"
              label={t('bulkTranslationFeature.settingsPage.typeBadgeNmt', 'Machine translation (NMT)')}
              sx={onBannerChip}
            />
          )}
          {/* far right of the heading row, like the help icon on every other page heading */}
          <SolutionsArticleLink
            article="bulkTranslation"
            subject={t('navigation.primary.bulkSettings')}
            placement="headingInline"
            rootStyle={{ position: 'relative', display: 'inline-block', marginLeft: 'auto', marginRight: 12, fontSize: 20 }}
          />
        </div>
        <Divider className="divider-white" />
        <div className={classes.pageGrid}>
          {/* Settings — xs: row 1, lg: left column */}
          <div id="bulkTranslationSetup" className={classes.settingsGrid}>
            <Paper className={classes.root} elevation={1}>
              <div className={classes.cardHeader}>
                <Typography variant="subtitle2" className={classes.cardTitle}>
                  {t('bulkTranslationFeature.settingsPage.settingsTitle')}
                </Typography>
              </div>
              <Divider style={{ marginBottom: 12 }} />
              <BulkTranslationSettings showInModalDialog={false} onConfigChange={this.handleConfigChange} />
            </Paper>
          </div>

          {/* Right column — config summary + word balance stacked */}
          <div className={classes.rightColumn}>
            {/* Config summary */}
            <Paper className={classes.root} elevation={1}>
              <div className={classes.cardHeader}>
                <Typography variant="subtitle2" className={classes.cardTitle}>
                  {t('bulkTranslationFeature.settingsPage.configSummaryTitle')}
                </Typography>
              </div>
              <Divider style={{ marginBottom: 12 }} />

              <Typography variant="caption" className={classes.label}>
                {t('bulkTranslationFeature.settingsPage.configType', 'Translation type')}
              </Typography>
              <div className={classes.chipRow} style={{ marginBottom: 12 }}>
                <Chip
                  icon={isAiBulk ? <AutoAwesomeOutlinedIcon /> : undefined}
                  color={isAiBulk ? 'success' : 'default'}
                  variant="outlined"
                  size="small"
                  className={classes.chip}
                  label={isAiBulk
                    ? t('bulkTranslationFeature.settingsPage.typeAi', 'AI (LLM)')
                    : t('bulkTranslationFeature.settingsPage.typeNmt', 'Machine translation (NMT)')}
                />
                {isAiBulk && (
                  <Chip
                    variant="outlined"
                    size="small"
                    className={classes.chip}
                    label={t('system.beta', 'Beta')}
                  />
                )}
              </div>
              {isAiBulk && (
                <Typography variant="body2" color="text.secondary" style={{ marginBottom: 12 }}>
                  {t('bulkTranslationFeature.settingsDialog.step3.aiInfo', 'Translated by AI — no words are deducted from your balance.')}
                  {' '}
                  {t('bulkTranslationFeature.settingsDialog.step3.aiNoCountInfo', 'A word count is therefore skipped.')}
                </Typography>
              )}

              {config ? (
                <>
                  <Typography variant="caption" className={classes.label}>
                    {t('bulkTranslationFeature.settingsPage.configLanguages')}
                  </Typography>
                  <div className={classes.chipRow} style={{ marginBottom: 12 }}>
                    {config.resolvedLanguages.map((lang, i) => (
                      <Chip key={i} label={lang.name} size="small" className={classes.chip} />
                    ))}
                  </div>

                  <Typography variant="caption" className={classes.label}>
                    {t('bulkTranslationFeature.settingsPage.configSections')}
                  </Typography>
                  <div className={classes.chipRow} style={{ marginBottom: config.outdated ? 12 : 0 }}>
                    {config.fields
                      .filter(f => !f.startsWith('theme.'))
                      .map((field, i) => {
                        if (field === 'themes') {
                          const themeFields = config.fields.filter(f => f.startsWith('theme.'));
                          if (themeFields.length > 0) {
                            return themeFields.map((tf, ti) => {
                              const themeId = tf.split('.')[1];
                              const theme = themes?.themes?.find(th => String(th.shopify_id) === String(themeId));
                              return (
                                <Chip
                                  key={`theme-${ti}`}
                                  // "Theme:" in front, as in the words log: a quirky theme name alone reads oddly
                                  label={theme ? t('wordslog.themeChip', { name: theme.name }) : t('bulkTranslationFeature.themeNotFound', { themeId })}
                                  size="small"
                                  className={classes.chip}
                                />
                              );
                            });
                          }
                        }
                        // "products.variants" is no key path; the sub-fields carry their own labels
                        return (
                          <Chip
                            key={i}
                            label={t(BULK_SUB_FIELD_LABELS[field] || `bulkTranslationFeature.settingsDialog.${field}`, field)}
                            size="small"
                            className={classes.chip}
                          />
                        );
                      })}
                  </div>

                  {config.outdated && (
                    <>
                      <Typography variant="caption" className={classes.label}>
                        {t('bulkTranslationFeature.settingsDialog.step2.outdatedLabel')}
                      </Typography>
                      <div className={classes.chipRow}>
                        <Chip label={t('system.enabled')} size="small" className={classes.chip} />
                      </div>
                    </>
                  )}
                </>
              ) : (
                <Typography variant="body2" className={classes.emptyState}>
                  {t('bulkTranslationFeature.settingsPage.configSummaryEmpty')}
                </Typography>
              )}
            </Paper>

            {/* Word balance */}
            <Paper className={classes.root} elevation={1}>
              <div className={classes.cardHeader}>
                <Typography variant="subtitle2" className={classes.cardTitle}>
                  {t('bulkTranslationFeature.settingsPage.wordBalanceTitle')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => this.setState({ showRechargePlans: true })}
                >
                  {t('bulkTranslationFeature.settingsPage.buyWords')}
                </Button>
              </div>
              <Divider style={{ marginBottom: 12 }} />

              {/*
                In AI mode the balance no longer drives bulk jobs, so the notice takes over the
                top of the box and the numbers lose their warning colours. They stay visible
                because single translations still consume words - only bulk is free.
              */}
              {isAiBulk && (
                <div className={classes.aiWordsNotice}>
                  <AutoAwesomeOutlinedIcon fontSize="small" color="success" />
                  <Typography variant="body2">
                    <strong>{t('bulkTranslationFeature.settingsPage.wordBalanceAiTitle', 'No words are charged in AI mode')}</strong>
                    {' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {t('bulkTranslationFeature.settingsPage.wordBalanceAiText', 'Bulk translations run free of charge, so nothing is counted or deducted. Your balance below still applies to single translations.')}
                    </Typography>
                  </Typography>
                </div>
              )}

              <div className={classes.statsRow}>
                <div className={classes.stat}>
                  <Typography variant="caption" className={classes.label}>{t('bulkTranslationFeature.settingsPage.available')}</Typography>
                  <Typography variant="h5" style={{ color: isAiBulk ? undefined : wordsColor }}>
                    {formatLargeNumber(availableWords)}
                  </Typography>
                </div>
                <div className={classes.stat}>
                  <Typography variant="caption" className={classes.label}>{t('bulkTranslationFeature.settingsPage.used')}</Typography>
                  <Typography variant="h5" className={classes.usedWords}>
                    {formatLargeNumber(totalUsedWords)}
                  </Typography>
                </div>
                {!isAiBulk && missingWords > 0 && (
                  <div className={classes.stat}>
                    <Typography variant="caption" className={classes.label}>{t('bulkTranslationFeature.settingsPage.missingWords')}</Typography>
                    <Typography variant="h5" className={classes.missingWords}>
                      {formatLargeNumber(missingWords)}
                    </Typography>
                  </div>
                )}
              </div>

              {wordsLogLoading ? (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <CircularProgress size={20} />
                </div>
              ) : wordsLog.length > 0 ? (
                <>
                  <Table size="small" style={{ marginBottom: 8 }}>
                    <TableBody>
                      {wordsLog.map((row) => (
                        <TableRow key={row.id} style={{ height: 28 }}>
                          {/* war rgba(0,0,0,0.54) inline — im Dark Mode 1.16:1 */}
                          <TableCell padding="none" sx={{ color: 'text.secondary', fontSize: 11, paddingRight: '8px', whiteSpace: 'nowrap' }}>
                            {formatDate(row.actionDate)}
                          </TableCell>
                          <TableCell padding="none" style={{ fontSize: 11, paddingRight: 8 }}>
                            {row.actionType}
                          </TableCell>
                          <TableCell padding="none" style={{ fontSize: 11, paddingRight: 8 }}>
                            {row.targetLanguage}
                          </TableCell>
                          <TableCell padding="none" align="right" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            {/* an AI run charges nothing: a zero is a fact, not a deduction */}
                            <span style={{ color: row.wordTransaction > 0 ? 'green' : row.wordTransaction < 0 ? 'red' : theme.palette.text.secondary, fontWeight: 'bold' }}>
                              {row.wordTransaction > 0 ? '+' : ''}{formatLargeNumber(row.wordTransaction)}
                            </span>
                            {' → '}
                            {formatLargeNumber(row.newWordTotal)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {wordsLogHasMore && (
                    <div style={{ textAlign: 'center', marginTop: 4 }}>
                      <Button
                        size="small"
                        onClick={() => this.props.history.push('/machinetranslation/wordslog')}
                      >
                        {t('system.showMore', 'Show more')}
                      </Button>
                    </div>
                  )}
                </>
              ) : null}

              <RechargePlans
                visible={showRechargePlans}
                wordNeeded={10000}
                onClose={() => this.setState({ showRechargePlans: false })}
                onBuy={onBuyWords}
              />
            </Paper>

            {/* Review notice */}
            <Paper className={classes.root} elevation={1}>
              <div className={classes.cardHeader}>
                <Typography variant="subtitle2" className={classes.cardTitle}>
                  {t('bulkTranslationFeature.settingsPage.reviewTitle')}
                </Typography>
              </div>
              <Divider style={{ marginBottom: 12 }} />
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                {t('bulkTranslationFeature.settingsPage.reviewIntro')}
              </Typography>
              <ul className={classes.reviewList}>
                {REVIEW_POINTS.map((key) => (
                  <li key={key}>
                    <Typography variant="body2">
                      <Trans i18nKey={`bulkTranslationFeature.settingsPage.${key}`} components={REVIEW_COMPONENTS} />
                    </Typography>
                  </li>
                ))}
              </ul>
              <Typography
                variant="body2"
                component="a"
                href={solutionsArticleUrl('machineTranslations')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('bulkTranslationFeature.settingsPage.reviewArticle')}
                <OpenInNewOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', ml: 0.5 }} />
              </Typography>
            </Paper>

          </div>
        </div>
      </React.Fragment>
    );
  }
}

const styles = theme => ({
  root: {
    width: '100%',
    padding: 24,
    [theme.breakpoints.down('md')]: {
      padding: 16,
    },
  },
  pageGrid: {
    marginTop: 20,
    display: 'grid',
    gap: 24,
    gridTemplateColumns: '1fr',
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '2fr 1fr',
      alignItems: 'start',
    },
  },
  settingsGrid: {},
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  cardTitle: {
    fontWeight: 600,
  },
  statsRow: {
    display: 'flex',
    gap: 24,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
    marginBottom: 4,
    display: 'block',
  },
  usedWords: {
    color: theme.palette.text.secondary,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  chip: {
    height: 22,
  },
  emptyState: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  missingWords: {
    color: theme.palette.error.main,
  },
  reviewList: {
    margin: '0 0 12px',
    paddingLeft: 20,
    '& li': { marginBottom: 6 },
  },
  aiWordsNotice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12,
    padding: 12,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
});

const mapStateToProps = ({ shop, languages, themes, bulkTranslation, config }) => ({
  shop,
  languages,
  themes,
  bulkTranslation,
  bulkSettingsData: config.bulk_settings_data,
});

export default withRouter(connect(mapStateToProps, { getBulkSettings })(withStyles(styles)(withTranslation()(MachineTranslations))));
